/**
 * Armador de combo de la ficha de producto.
 *
 * Suma la faja que está viendo más las que marque, busca el escalón de descuento
 * que le corresponde y muestra el ahorro en dinero. El cálculo replica el del
 * checkout: Shopify aplica UN solo descuento de producto, el del escalón más
 * alto que alcance la cantidad total.
 *
 * Las tallas de las demás fajas siguen a la que eligió arriba. Sin eso, todas
 * se iban en la primera talla de la lista (XS) aunque ella estuviera mirando
 * una L: se llevaba dos fajas que no le sirven sin enterarse.
 *
 * Va como módulo para poder importar CartAddEvent del tema. Cargado como script
 * clásico, `CartAddEvent` no existe en la ventana, no se avisaba a nadie y el
 * cajón del carrito no se abría: la faja entraba, pero parecía que no.
 *
 * Los escalones, el precio base y el formato de dinero vienen del servidor, no
 * se escriben aquí: si el ajuste del tema cambia, este cálculo cambia con él.
 */
import { CartAddEvent, ThemeEvents } from '@theme/events';

/** '2:10,3:15' -> [{unidades: 2, pct: 10}, {unidades: 3, pct: 15}] */
const leerEscalones = (texto) =>
  String(texto || '')
    .split(',')
    .map((parte) => {
      const [unidades, pct] = parte.split(':');
      return { unidades: Number(unidades), pct: Number(pct) };
    })
    .filter((e) => Number.isFinite(e.unidades) && Number.isFinite(e.pct) && e.unidades > 0)
    .sort((a, b) => a.unidades - b.unidades);

/** '123:XS,124:S' -> Map { '123' => 'XS', '124' => 'S' } */
const leerTallas = (texto) =>
  new Map(
    String(texto || '')
      .split(',')
      .map((parte) => {
        const corte = parte.indexOf(':');
        return corte < 0 ? null : [parte.slice(0, corte).trim(), parte.slice(corte + 1).trim()];
      })
      .filter(Boolean)
  );

/**
 * Escribe los centavos con el formato de la tienda ("${{amount}}", etc.), no
 * con el del navegador: un "15,50 US$" al lado de los "$84.99" de la ficha
 * parece un error de la página.
 */
const formateador = (formato) => {
  const miles = (entero, separador) => entero.replace(/\B(?=(\d{3})+(?!\d))/g, separador);

  return (centavos) => {
    const n = Math.abs(Number(centavos) || 0) / 100;
    const [ent, dec] = n.toFixed(2).split('.');
    const sinDecimales = Math.round(n).toString();

    const valores = {
      amount: miles(ent, ',') + '.' + dec,
      amount_no_decimals: miles(sinDecimales, ','),
      amount_with_comma_separator: miles(ent, '.') + ',' + dec,
      amount_no_decimals_with_comma_separator: miles(sinDecimales, '.'),
      amount_with_apostrophe_separator: miles(ent, "'") + '.' + dec,
      amount_no_decimals_with_space_separator: miles(sinDecimales, ' '),
      amount_with_space_separator: miles(ent, ' ') + ',' + dec,
      amount_with_period_and_space_separator: miles(ent, ' ') + '.' + dec,
    };

    const texto = String(formato || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/g, (todo, clave) =>
      clave in valores ? valores[clave] : todo
    );
    // El formato puede traer etiquetas (<span class="money">): aquí solo se
    // quiere el número, el estilo lo pone el CSS del bloque.
    return texto.replace(/<[^>]*>/g, '').trim();
  };
};

const montar = (raiz) => {
  if (raiz.dataset.ecComboListo === 'true') return;
  raiz.dataset.ecComboListo = 'true';

  const escalones = leerEscalones(raiz.dataset.tiers);
  const tallasDeVariante = leerTallas(raiz.dataset.variantSizes);
  const base = Number(raiz.dataset.basePrice) || 0;
  const resumen = raiz.querySelector('[data-ec-combo-summary]');
  const aviso = raiz.querySelector('[data-ec-combo-mixed]');
  const etiquetaTalla = raiz.querySelector('[data-ec-combo-current-size]');
  const boton = raiz.querySelector('[data-ec-combo-add]');
  const filas = Array.from(raiz.querySelectorAll('[data-ec-combo-item]'));
  const pastillas = Array.from(raiz.querySelectorAll('[data-ec-combo-tier]'));
  if (!escalones.length || !resumen || !boton) return;

  const dinero = formateador(raiz.dataset.moneyFormat);
  // El hueco es '%%' y no '#': un '#' se colaba dentro de entidades como &#39;
  // y se sustituía ahí, partiendo el apóstrofo del texto en inglés.
  const rellenar = (plantilla, valor) => String(plantilla || '').replace('%%', valor);

  const marcadas = () => filas.filter((f) => f.querySelector('[data-ec-combo-pick]')?.checked);

  /** La variante de la faja que está viendo, tomada del formulario principal. */
  const varianteActual = () => {
    const campo =
      document.querySelector('product-form-component input[name="id"]') ||
      document.querySelector('form[action*="/cart/add"] input[name="id"]') ||
      document.querySelector('form[action*="/cart/add"] select[name="id"]');
    return campo ? campo.value : null;
  };

  const tallaActual = () => tallasDeVariante.get(String(varianteActual() || '')) || '';

  /**
   * Pone cada faja en la talla que eligió arriba. Si ese modelo no la tiene o
   * está agotada, se deja como estaba y la fila lo dice.
   */
  const sincronizarTallas = () => {
    const talla = tallaActual();
    if (etiquetaTalla) etiquetaTalla.textContent = talla;
    if (!talla) return;

    filas.forEach((fila) => {
      const select = fila.querySelector('[data-ec-combo-size]');
      const nota = fila.querySelector('[data-ec-combo-note]');
      if (!select) return;

      const opcion = Array.from(select.options).find((o) => o.dataset.size === talla && !o.disabled);
      // Solo se mueve si ella no la ha tocado: su elección manda sobre la nuestra.
      if (opcion && select.dataset.tocado !== 'true') select.value = opcion.value;

      if (nota) {
        const falta = !opcion;
        nota.hidden = !falta;
        nota.textContent = falta ? rellenar(raiz.dataset.labelMissing, talla) : '';
      }
    });
  };

  const calcular = () => {
    const elegidas = marcadas();
    const piezas = 1 + elegidas.length;
    const subtotal = elegidas.reduce((suma, f) => suma + (Number(f.dataset.price) || 0), base);

    // El escalón más alto que alcanza: es lo que hará el checkout.
    const escalon = [...escalones].reverse().find((e) => piezas >= e.unidades);
    const pct = escalon ? escalon.pct : 0;
    // Truncado, no redondeado: así descuenta Shopify. Redondear hacia arriba
    // prometería un centavo que el checkout no da.
    const ahorro = Math.floor((subtotal * pct) / 100);

    return {
      piezas,
      subtotal,
      pct,
      alcanzado: escalon ? escalon.unidades : 0,
      ahorro,
      total: subtotal - ahorro,
      siguiente: escalones.find((e) => e.unidades > piezas),
    };
  };

  /** Las tallas que se llevaría ahora mismo, sin repetir y en orden de aparición. */
  const tallasElegidas = () => {
    const vistas = [];
    const anotar = (t) => {
      if (t && !vistas.includes(t)) vistas.push(t);
    };
    anotar(tallaActual());
    marcadas().forEach((f) => {
      const select = f.querySelector('[data-ec-combo-size]');
      anotar(select?.selectedOptions[0]?.dataset.size);
    });
    return vistas;
  };

  const pintar = () => {
    const r = calcular();
    boton.disabled = r.piezas < 2;

    // Se marca el escalón en el que está parada, para que lo vea de un vistazo.
    pastillas.forEach((p) => {
      p.classList.toggle('ec-combo__escalon--activo', Number(p.dataset.ecComboTier) === r.alcanzado);
    });

    if (r.piezas < 2) {
      resumen.className = 'ec-combo__resumen';
      resumen.textContent = raiz.dataset.labelHint || '';
    } else {
      resumen.className = 'ec-combo__resumen ec-combo__resumen--activo';
      // El ahorro va primero y grande: es el dato que decide.
      resumen.innerHTML =
        `<strong><i>${raiz.dataset.labelSaving || ''}</i>${dinero(r.ahorro)}</strong>` +
        `<span>${rellenar(raiz.dataset.labelPieces, r.piezas)} · <s>${dinero(r.subtotal)}</s> ${dinero(
          r.total
        )}</span>` +
        (r.siguiente ? `<em>+1 → −${r.siguiente.pct}%</em>` : '');
    }

    // Mezclar tallas puede ser a propósito (un regalo), así que se avisa y ya.
    if (aviso) {
      const tallas = tallasElegidas();
      const mezcla = r.piezas >= 2 && tallas.length > 1;
      aviso.hidden = !mezcla;
      aviso.textContent = mezcla ? rellenar(raiz.dataset.labelMixed, tallas.join(' · ')) : '';
    }

    boton.textContent =
      r.piezas < 2 ? raiz.dataset.labelAdd : `${raiz.dataset.labelAdd} · ${r.piezas}`;
  };

  raiz.addEventListener('change', (evento) => {
    if (evento.target.matches('[data-ec-combo-size]')) evento.target.dataset.tocado = 'true';
    if (evento.target.matches('[data-ec-combo-pick], [data-ec-combo-size]')) pintar();
  });

  // Cuando cambia la talla arriba, las de abajo la siguen.
  document.addEventListener(ThemeEvents.variantUpdate, () => {
    sincronizarTallas();
    pintar();
  });

  boton.addEventListener('click', async () => {
    const elegidas = marcadas();
    if (!elegidas.length) return;

    const items = [];
    const actual = varianteActual();
    if (actual) items.push({ id: Number(actual), quantity: 1 });
    elegidas.forEach((f) => {
      const id = Number(f.querySelector('[data-ec-combo-size]')?.value);
      if (id) items.push({ id, quantity: 1 });
    });
    if (!items.length) return;

    const etiqueta = boton.textContent;
    boton.disabled = true;
    boton.setAttribute('aria-busy', 'true');
    boton.textContent = raiz.dataset.labelBusy || etiqueta;

    const secciones = Array.from(document.querySelectorAll('cart-items-component'))
      .map((n) => n.dataset.sectionId)
      .filter(Boolean)
      .join(',');
    const url = (typeof Theme !== 'undefined' && Theme?.routes?.cart_add_url) || '/cart/add.js';

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: secciones }),
      });
      const datos = await res.json();

      if (!res.ok || datos.status) {
        boton.textContent = raiz.dataset.labelError || etiqueta;
        return;
      }

      // Esto es lo que refresca el ícono del carrito y abre el cajón.
      boton.dispatchEvent(
        new CartAddEvent(datos, 'ec-combo', {
          source: 'eclipse-combo',
          itemCount: items.length,
          sections: datos.sections,
        })
      );

      boton.textContent = raiz.dataset.labelDone || etiqueta;

      window.Shopify?.analytics?.publish?.('eclipse_bundle_add', {
        source: 'product_page_combo',
        items: items.length,
        discount_percent: calcular().pct,
      });
    } catch (error) {
      console.error(error);
      boton.textContent = raiz.dataset.labelError || etiqueta;
    } finally {
      boton.removeAttribute('aria-busy');
      window.setTimeout(() => {
        filas.forEach((f) => {
          const pick = f.querySelector('[data-ec-combo-pick]');
          if (pick) pick.checked = false;
        });
        pintar();
      }, 1800);
    }
  });

  sincronizarTallas();
  pintar();
};

const iniciar = (ambito = document) => ambito.querySelectorAll('[data-ec-combo]').forEach(montar);

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => iniciar());
else iniciar();
document.addEventListener('shopify:section:load', (evento) => iniciar(evento.target));
